import {
  SoftwareCostOut,
  SoftwareOut,
  SoftwareTypes,
  SoftwareUserCount,
} from '@/client/index.js'
import { db } from '../database.js'

export interface ApplicationTable {
  tags: string // JSON string representing an array of strings
  created: string // ISO 8601 formatted date-time string
  updated: string // ISO 8601 formatted date-time string
  deleted: number // Boolean stored as integer (0 or 1)
  deleted_at: string | null // Nullable ISO 8601 date-time string
  _id: string // Unique identifier, possibly a UUID
  software_name: string // Internal name of the software
  desc_short: string | null
  display_name: string // Display name of the software
  type: string | null // SoftwareTypes as a string
  read_only: number // Boolean stored as integer (0 or 1)
  desc: string | null
  default_licenses: string // JSON string representing an array of strings
  data: string | null // JSON string or null
  notes: string | null
  sub_accounts: string // JSON string representing a Record<string, SubAccount_Output>
  licence_groups: string // JSON string representing a Record<string, LicenceGroup>
  connected_account: string | null
  authentication_expired: number // Boolean stored as integer (0 or 1)
  license_features: string // JSON string representing a Record<string, SoftwareLicenseFeature_Output>
  config: string // JSON string representing SoftwareConfig
  awaits_authorization: number // Boolean stored as integer (0 or 1)
  information: string // JSON string representing SoftwareInformation
  sso_provider: number // Boolean stored as integer (0 or 1)
  owner_account_ids: string // JSON string representing an array of strings
  sub_accounts_available: string // JSON string representing available sub accounts
}

export async function createApplicationTable() {
  await db.schema
    .createTable('application')
    .ifNotExists()
    .addColumn('_id', 'text', (col) => col.primaryKey().notNull())
    .addColumn('tags', 'text', (col) => col.notNull()) // JSON string
    .addColumn('created', 'text', (col) => col.notNull()) // ISO 8601 string
    .addColumn('updated', 'text', (col) => col.notNull()) // ISO 8601 string
    .addColumn('deleted', 'integer', (col) => col.notNull()) // Boolean as integer
    .addColumn('deleted_at', 'text') // Nullable ISO 8601 string
    .addColumn('software_name', 'text', (col) => col.notNull())
    .addColumn('desc_short', 'text')
    .addColumn('display_name', 'text', (col) => col.notNull())
    .addColumn('type', 'text') // Nullable string
    .addColumn('read_only', 'integer', (col) => col.notNull()) // Boolean as integer
    .addColumn('desc', 'text')
    .addColumn('default_licenses', 'text', (col) => col.notNull()) // JSON string
    .addColumn('data', 'text') // JSON string or null
    .addColumn('notes', 'text')
    .addColumn('sub_accounts', 'text', (col) => col.notNull()) // JSON string
    .addColumn('licence_groups', 'text', (col) => col.notNull()) // JSON string
    .addColumn('connected_account', 'text') // Nullable string
    .addColumn('authentication_expired', 'integer', (col) => col.notNull()) // Boolean as integer
    .addColumn('license_features', 'text', (col) => col.notNull()) // JSON string
    .addColumn('config', 'text', (col) => col.notNull()) // JSON string
    .addColumn('awaits_authorization', 'integer', (col) => col.notNull()) // Boolean as integer
    .addColumn('information', 'text', (col) => col.notNull()) // JSON string
    .addColumn('owner_account_ids', 'text', (col) => col.notNull()) // JSON string
    .addColumn('sub_accounts_available', 'text', (col) => col.notNull()) // JSON string
    .addColumn('sso_provider', 'integer', (col) => col.notNull().defaultTo(0)) // Boolean as integer
    .execute()

  // Index auf der Spalte 'software_name' erstellen
  await db.schema
    .createIndex('idx_software_out_name')
    .ifNotExists()
    .on('application')
    .column('software_name')
    .execute()
}

// Helper function to serialize SoftwareOut for database storage
export function serializeApplication(
  application: SoftwareOut
): ApplicationTable {
  return {
    tags: JSON.stringify(application.tags),
    created: application.created,
    updated: application.updated,
    deleted: application.deleted ? 1 : 0,
    deleted_at: application.deleted_at ?? null,
    _id: application._id,
    software_name: application.software_name,
    desc_short: application.desc_short ?? null,
    display_name: application.display_name,
    type: application.type ?? null,
    read_only: application.read_only ? 1 : 0,
    desc: application.desc ?? null,
    default_licenses: JSON.stringify(application.default_licenses),
    data: application.data ? JSON.stringify(application.data) : null,
    notes: application.notes ?? null,
    sub_accounts: JSON.stringify(application.sub_accounts),
    licence_groups: JSON.stringify(application.licence_groups),
    connected_account: application.connected_account ?? null,
    authentication_expired: application.authentication_expired ? 1 : 0,
    license_features: JSON.stringify(application.license_features),
    config: JSON.stringify(application.config),
    awaits_authorization: application.awaits_authorization ? 1 : 0,
    information: JSON.stringify(application.information),
    owner_account_ids: JSON.stringify(application.owner_account_ids),
    sub_accounts_available: JSON.stringify(application.sub_accounts_available),
    sso_provider: application.sso_provider ? 1 : 0,
  }
}

// Helper function to deserialize database record into SoftwareOut
export function deserializeApplication(
  record: ApplicationTable & {
    subaccount_user_count?: string
    sub_account_costs?: string
  },
  join: boolean = false
): SoftwareOut {
  return {
    ...record,
    tags: JSON.parse(record.tags),
    deleted: Boolean(record.deleted),
    deleted_at: record.deleted_at ?? null,
    read_only: Boolean(record.read_only),
    default_licenses: JSON.parse(record.default_licenses),
    data: record.data ? JSON.parse(record.data) : null,
    sub_accounts: JSON.parse(record.sub_accounts),
    licence_groups: JSON.parse(record.licence_groups),
    authentication_expired: Boolean(record.authentication_expired),
    license_features: JSON.parse(record.license_features),
    config: JSON.parse(record.config),
    awaits_authorization: Boolean(record.awaits_authorization),
    information: JSON.parse(record.information),
    owner_account_ids: JSON.parse(record.owner_account_ids),
    sub_accounts_available: JSON.parse(record.sub_accounts_available),
    sso_provider: Boolean(record.sso_provider),
    type: record.type as SoftwareTypes, // Cast `type` to the correct type
    ...(join && {
      ...(record.subaccount_user_count && {
        subaccount_user_count: JSON.parse(record.subaccount_user_count),
      }),
      ...(record.sub_account_costs && {
        sub_account_costs: JSON.parse(record.sub_account_costs),
      }),
    }),
  }
}

// ----------------------------------------------------------------------
// Application User Count
// ----------------------------------------------------------------------
export interface ApplicationUserCountTable {
  _id: string
  total: number | null
  sub_accounts: string | null // JSON string representing a Record<string, number>
}

export async function createApplicationUserCountTable() {
  await db.schema
    .createTable('application_usercount')
    .ifNotExists()
    .addColumn('_id', 'text', (col) => col.primaryKey().notNull()) // Unique identifier
    .addColumn('total', 'integer') // Total user count
    .addColumn('sub_accounts', 'text', (col) => col.notNull()) // JSON string representing Record<string, number>
    .execute()

  // Index on the '_id' column
  await db.schema
    .createIndex('idx_application_usercount_id')
    .ifNotExists()
    .on('application_usercount')
    .column('_id')
    .execute()
}

// Helper function to serialize SoftwareUserCount for database storage
export function serializeApplicationUserCount(
  id: string,
  record: SoftwareUserCount
): { _id: string; total: number; sub_accounts: string } {
  return {
    _id: id,
    total: record.total ?? 0,
    sub_accounts: JSON.stringify(record.sub_accounts),
  }
}

// Helper function to deserialize database records into Record<string, SoftwareUserCount>
export function deserializeApplicationUserCount(
  record: ApplicationUserCountTable
): Record<string, SoftwareUserCount> {
  return {
    [record._id]: {
      total: record.total ?? 0,
      sub_accounts: record.sub_accounts ? JSON.parse(record.sub_accounts) : {},
    },
  }
}

// ----------------------------------------------------------------------
// Application Costs
// ----------------------------------------------------------------------
export interface ApplicationCostsTable {
  _id: string
  total: number
  sub_account_costs: string | null // JSON string representing a Record<string, number>
}

export async function createApplicationCostsTable() {
  await db.schema
    .createTable('application_costs')
    .ifNotExists()
    .addColumn('_id', 'text', (col) => col.primaryKey().notNull()) // Unique identifier
    .addColumn('total', 'integer') // Total user count
    .addColumn('sub_account_costs', 'text', (col) => col.notNull()) // JSON string representing Record<string, number>
    .execute()

  // Index on the '_id' column
  await db.schema
    .createIndex('idx_application_costs_id')
    .ifNotExists()
    .on('application_costs')
    .column('_id')
    .execute()
}

// Helper function to serialize SoftwareUserCount for database storage
export function serializeApplicationCosts(
  id: string,
  record: SoftwareCostOut
): { _id: string; total: number; sub_account_costs: string } {
  return {
    _id: id,
    total: record.total,
    sub_account_costs: JSON.stringify(record.sub_account_costs),
  }
}

// Helper function to deserialize database records into Record<string, SoftwareUserCount>
export function deserializeApplicationCosts(
  record: ApplicationCostsTable
): Record<string, SoftwareCostOut> {
  return {
    [record._id]: {
      total: record.total,
      sub_account_costs: record.sub_account_costs
        ? JSON.parse(record.sub_account_costs)
        : undefined,
    },
  }
}
